import React, { useState, useEffect, useMemo, useRef } from 'react';
// import Sidebar from './Sidebar'; // 사이드바 미사용 주석
import Dashboard from './components/Dashboard';
import ContactList from './components/ContactList'; // 고객 관리 (개인 담당자)
import CompanyList from './components/CompanyList'; // 🚀 [신규 예정] 거래처 관리 (학교/업체)
import DealBoard from './components/DealBoard';
// import LeadBoard from './components/LeadBoard'; // ❌ 영업리드 미사용으로 주석 처리됨 (삭제 완료).
import SalesTarget from './components/SalesTarget';
import SalesForecast from './components/SalesForecast';   // 💰 매출 관리 (Forecast)
import SalesAnalysis from './components/SalesAnalysis';   // 📈 매출 세부 내역 (Analysis)
//  import SalesHistory from './components/SalesHistory';   // 🕒 매출 이력 관리 (삭제됨)
import SalesSupport from './components/SalesSupport';
import ActivityLog from './components/ActivityLog';
import Reports from './components/Reports';
import Settings from './components/Settings';
import AIAssistant from './components/AIAssistant';
import SupportAuth from './components/SupportAuth';
import ProjectProgressBoard from './components/ProjectProgressBoard';

// 👇 [신규 추가] CS 관리 화면 임포트
import CSActivityManagement from './components/CSActivityManagement'; 
// 📅 [신규 추가] 통합 달력 화면 임포트
import UnifiedCalendar from './components/UnifiedCalendar'; 

import PermissionGate from './components/PermissionGate';
//이탈 거래처
import LostDeals from './components/LostDeals';

//나라장터 수집관리
import G2BAdminBoard from './components/admin/G2BAdminBoard';

// 상수 및 타입 정의
import { MOCK_DEALS, MOCK_ACTIVITIES, MOCK_CONTACTS, INITIAL_SALES_REPS } from './constants'; 
import { Contact, Deal, ViewState, User, SalesRep, InternalHistoryRecord, CompetitorHistoryRecord, Activity, CSActivity, ProjectProgress } from './types';
import { crmDataService } from './services/crmDataService';
import { getFormattedDateTime } from './services/dateService';
// 🚨 [수정] 동기화(RefreshCw) 버튼은 실시간 업데이트로 인해 삭제되었습니다.
import { Bell, Clock, Loader2, Shield, Menu, XCircle } from 'lucide-react'; 

// 관리자 모드 컴포넌트
import AdminLayout from './components/admin/AdminLayout';

// API 통합관리
import { API_BASE_URL } from './config';

// 👇 🚨 [여기에 추가] 슈퍼베이스 클라이언트 임포트 및 셋팅
import { createClient } from '@supabase/supabase-js';

// 슈퍼베이스 URL과 익명 키는 클라우드 변수에서 추가했습니다. (보안상 직접 코드에 쓰지 않습니다)
// const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || ''; 
// const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || ''; 
const supabaseUrl = 'https://sniypplksnrhplneugtr.supabase.co'; 
const supabaseKey = 'sb_publishable_6tSAOmfJ4hfbge2i_2VwPw_B7-rYhLt'; // 이미지 9번의 전체 키 입력
const supabase = createClient(supabaseUrl, supabaseKey);

// // 백엔드 API 주소
// const API_BASE_URL = 'http://localhost:8000';

const App: React.FC = () => {
  // ==================================================================================
  // [SECTION 1] 상태 관리 (State Management)
  // ==================================================================================
  
  // 1-1. 화면 전환 상태 (기본값: 대시보드)
  const [currentView, setCurrentView] = useState<ViewState | 'companies' | 'g2b'| string>('dashboard');

  // 🚀 [최상단 신규 추가] 사이드바 숨김/펼침 상태 (기본값: false = 숨김)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // 👇 [신규 추가] 자식 화면들에게 보내는 "새로고침 신호탄"
  const [globalRefreshKey, setGlobalRefreshKey] = useState(0);

  // 🚨 [핵심 추가] 알림창에서 클릭한 CS 항목을 전달하기 위한 바통(상태)
  const [targetCsActivity, setTargetCsActivity] = useState<any>(null);

  // 1-2. 인증(로그인) 상태
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const auto = localStorage.getItem('ieg_auto_login') === 'true';
    const auth = localStorage.getItem('ieg_authenticated') === 'true';
    return auto && auth;
  });  
  const [authUsername, setAuthUsername] = useState<string>(() => localStorage.getItem('ieg_auth_username') || '');
  
  // 1-3. 현재 로그인한 사용자 정보 (🚨 role 필드 보강)
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('ieg_user_profile');
    if (saved) {
      const user = JSON.parse(saved);
      // 권한 시스템 작동을 위해 role이 없으면 기본 'admin' 부여 (임시)
      return user;
    }
    return null;
  });

  // 1-4. CRM 데이터 상태 (고객, 영업기회, 활동로그 등)
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  // const [leads, setLeads] = useState<Lead[]>([]); // ❌ Lead 상태 삭제됨
  const [activities, setActivities] = useState<Activity[]>([]);
  const [internalHistory, setInternalHistory] = useState<InternalHistoryRecord[]>([]);
  const [competitorHistory, setCompetitorHistory] = useState<CompetitorHistoryRecord[]>([]);
  
  // 🎯 [WBS 데이터 상태 추가] 
  const [wbsProjects, setWbsProjects] = useState<ProjectProgress[]>([]);

  // 👇 [신규 추가] DB에서 가져온 CS 데이터를 담을 바구니
  const [csActivities, setCsActivities] = useState<any[]>([]); 

  // 1-5. UI 상태 (로딩, 동기화, 알림 등)
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [currentTime, setCurrentTime] = useState<string>(getFormattedDateTime());

  // 알림창 외부 클릭 감지 로직
  const notiRef = useRef<HTMLDivElement>(null);


  // ==================================================================================
  // [SECTION 2] 인증 및 로그인 로직 (Auth Logic)
  // ==================================================================================

  // 2-1. 로그아웃 (상태 초기화)
  const clearAuthState = () => {
    localStorage.removeItem('ieg_authenticated');
    localStorage.removeItem('ieg_auth_username');
    localStorage.removeItem('ieg_user_profile');
    setIsAuthenticated(false);
    setAuthUsername('');
    setCurrentUser(null);
    setCurrentView('dashboard');
  };

  // 2-2. 토큰/세션 검증 (자동 로그인 시 실행)
  const verifyAuth = async () => {
    const username = authUsername || localStorage.getItem('ieg_auth_username') || '';
    if (!username) {
      clearAuthState();
      return false;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile/${encodeURIComponent(username)}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || '프로필 조회 실패');
      
      const dbUser = data?.user;
      if (!dbUser?.username) throw new Error('사용자 정보 없음');

      const verifiedUser: User = {
        id: dbUser.id,
        username: dbUser.username,
        name: dbUser.name || '',
        email: dbUser.email || '',
        phone: dbUser.phone || '',
        role: dbUser.role || 'user', // 🚨 서버에서 받은 role 적용
        permissions: dbUser.permissions || {}, // 🛡️ [추가] 7대장 권한 뭉치 받기
        team_name: dbUser.team_name || '', // ✅ 추가
        grade: dbUser.grade || ''          // ✅ 추가
      };
      
      setCurrentUser(verifiedUser);
      setAuthUsername(dbUser.username);
      localStorage.setItem('ieg_auth_username', dbUser.username);
      localStorage.setItem('ieg_user_profile', JSON.stringify(verifiedUser));
      return true;
    } catch {
      clearAuthState();
      return false;
    }
  };

  // 2-3. 로그인 성공 핸들러
  const handleLoginSuccess = (user?: { id?: number; username?: string; name?: string; email?: string; phone?: string; role?: string; permissions?: any; team_name?: string; grade?: string }) => {
    if (user?.name || user?.email) {
      const newUser: User = {
        id: user?.id,
        username: user?.username || '',
        name: user?.name || (currentUser?.name || 'Unknown'),
        email: user?.email || (currentUser?.email || ''),
        phone: user?.phone || (currentUser?.phone || ''),
        role: user?.role || 'user', // 🚨 로그인 시 권한 부여
        permissions: user?.permissions || {}, // 🛡️ [추가] 로그인 주머니에 권한 넣기
        team_name: user?.team_name || '', // ✅ 추가
        grade: user?.grade || ''          // ✅ 추가
      };
      setCurrentUser(newUser);
      localStorage.setItem('ieg_user_profile', JSON.stringify(newUser));
    }
    const username = user?.username || '';
    setAuthUsername(username);
    localStorage.setItem('ieg_auth_username', username);
    localStorage.setItem('ieg_authenticated', 'true');
    setIsAuthenticated(true);
    setCurrentView('dashboard');
  };

  const handleLogout = () => {
    clearAuthState();
  };


  // ==================================================================================
  // [SECTION 3] 데이터 로딩 (Data Fetching)
  // ==================================================================================

  // 🚨 [수정] 데이터 로딩 최적화를 위해 2단계 병렬 구조로 변경
  const loadData = async (isBackground = false) => {
    // 백그라운드 업데이트가 아닐 때(처음 켤 때)만 로딩 화면을 켭니다.
    if (!isBackground) {
      setIsLoading(true);
    }

    try {
      // 🚀 [최적화 1단계] 홈 화면(Dashboard) 로딩에 즉각 필요한 최소 데이터만 먼저 로딩
      const [fetchedDeals, fetchedUsers] = await Promise.all([
        crmDataService.getDeals(),
        crmDataService.getAllUsers()
      ]);

      // 1단계 데이터 즉시 반영
      setDeals(fetchedDeals || []);
      
      // 담당자 목록 세팅
      if (fetchedUsers && fetchedUsers.length > 0) {
        const realReps = fetchedUsers.map((u: any) => ({
          id: String(u.id),
          name: u.name,
          team: u.team_name || '미배정'
        }));
        setSalesReps(realReps);
      } else {
        setSalesReps([]);
      }

      // 🚨 핵심 포인트: 1단계 데이터(홈화면용)만 오면 로딩바를 먼저 꺼버립니다!
      if (!isBackground) {
        setIsLoading(false);
      }

      // 🚀 [최적화 2단계] 나머지 무거운 데이터는 백그라운드에서 조용히 가져오기
      Promise.all([
        crmDataService.getContacts(),
        crmDataService.getSalesHistory('internal'),
        crmDataService.getSalesHistory('competitor'),
        crmDataService.getAllDealActivities(),
        crmDataService.getCSActivities(),
        crmDataService.getProjectProgress()
      ]).then(([fetchedContacts, fetchedIntHistory, fetchedCompHistory, fetchedActivitiesObj, fetchedCSActivities, fetchedWBS]) => {
        
        // 2단계 데이터 차례로 반영
        setContacts(fetchedContacts || []); 
        setInternalHistory(fetchedIntHistory || []);
        setCompetitorHistory(fetchedCompHistory || []);
        setCsActivities(fetchedCSActivities || []); 
        setWbsProjects(fetchedWBS || []); 
        
        if (fetchedActivitiesObj) {
          const activitiesArray = Object.values(fetchedActivitiesObj);
          setActivities(activitiesArray as Activity[]);
        } else {
          setActivities([]);
        }

        // 모든 데이터 수신 완료 후 신호탄 숫자를 올려서 자식들에게 최종 알림
        setGlobalRefreshKey(prev => prev + 1);
        console.log("🚀 [Background] 모든 데이터 로드 완료");
      });

    } catch (error) {
      console.error("Data Load Error:", error);
      setContacts(MOCK_CONTACTS);
      setDeals(MOCK_DEALS);
      // 에러 시에도 로딩바는 꺼줌
      if (!isBackground) setIsLoading(false);
    }
  };

  // 초기 실행: 인증 확인 후 데이터 로드
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }
      const ok = await verifyAuth();
      if (ok && !cancelled) {
        loadData();
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ✨ [추가] 슈퍼베이스 전체 테이블 실시간 감시 설정 (기존 코드 100% 유지)
  useEffect(() => {
    // 로그인이 안 되어 있으면 감지기 작동 안 함
    if (!isAuthenticated) return;

    console.log("🌍 [Global] Supabase 전역 감지기 작동 준비 완료!");

    const channel = supabase
      .channel('global-db-changes')
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public' // table 속성을 지워서 모든 테이블을 감시합니다!
        },
        (payload) => {
          console.log(`🚨 [${payload.table}] 테이블 변경 감지됨! 화면을 갱신합니다.`);
          
          // 승호님이 이미 만들어두신 '전체 데이터 다시 불러오기' 함수!
          loadData(true); 
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated]);

  // 시계 타이머
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(getFormattedDateTime()), 1000);
    return () => clearInterval(timer);
  }, []);



  //종모양 알림 버튼 기능(바깥누르면은 사라지게)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 클릭한 곳이 notiRef(알림창) 바깥이면 알림창 닫기!
      if (notiRef.current && !notiRef.current.contains(event.target as Node)) {
        setShowAlerts(false);
      }
    };
    if (showAlerts) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAlerts]);

  // 화면(메뉴)이 바뀌어도 알림창 닫기
  useEffect(() => {
    setShowAlerts(false);
  }, [currentView]);

  


  // ==================================================================================
  // [SECTION 4] 알림 계산 로직 (Notifications)
  // ==================================================================================
  
  const alerts = useMemo(() => {
    const results: { type: string; title: string; deal?: Deal; msg: string; csActivity?: any }[] = [];
    const now = new Date();
    
    // 1. 기존: 입찰 마감일 알림
    deals.forEach(deal => {
      if (!deal.expectedCloseDate) return;
      const expDate = new Date(deal.expectedCloseDate);
      const diffTime = expDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (deal.value >= 100000000 && diffDays <= 45 && deal.stage === '미정') {
        results.push({ type: 'danger', title: '입찰공고 미조치 위험 (1억↑)', deal, msg: `매출예정 45일 전입니다. 조치가 필요합니다.` });
      }
    });

    // 2. 🚨 [신규 추가] CS팀 영업 전달 사항 알림
    csActivities.forEach(cs => {
      // 영업 전달 사항이 적혀 있는 경우만!
      if (cs.salesNote && cs.salesNote.trim() !== '' && cs.status !== '완료' && cs.status !== '매출등록') {
        
        // 1. 내 이름으로 지정된 건인지 확인
        const isMyDeal = cs.salesRep && cs.salesRep.includes(currentUser?.name || '');
        
        // 2. 아직 아무도 배정받지 않은 건인지 확인
        const isUnassigned = !cs.salesRep || cs.salesRep === '미정' || cs.salesRep === '';

        // 🚨 [핵심 추가] 현재 로그인한 사람이 '관리자(admin)'이거나 직급이 '팀장' 이상인지 확인
        const isManager = currentUser?.role === 'admin' || 
                          currentUser?.grade === '팀장' || 
                          currentUser?.grade === '본부장' || 
                          currentUser?.grade === '대표이사';

        // 분기 처리
        if (isMyDeal) {
          // 👉 [사원/관리자 공통] 나한테 배정된 건 무조건 알림이 뜸
          results.push({ 
            type: 'cs_info', 
            title: `🔔 [CS 연계] ${cs.company} 신규 기회`, 
            msg: `CS전달: "${cs.salesNote}"`,
            csActivity: cs
          });
        } else if (isUnassigned && isManager) { 
          // 👉 [관리자 전용] 미배정 건은 "isManager"가 true일 때만 뜸!
          results.push({ 
            type: 'cs_warning', 
            title: `📢 [미배정 CS기회] ${cs.company} 담당자 지정 필요`, 
            msg: `CS전달: "${cs.salesNote}"`,
            csActivity: cs
          });
        }
      }
    });

    return results;
  }, [deals, csActivities, currentUser]);

   // ==================================================================================
  // [SECTION 4-B] CS팀 활동 추가 핸들러 (삭제 및 전환)
  // ==================================================================================

  // 1. CS 활동 삭제 함수
  const handleDeleteCSActivity = async (id: string) => {
    try {
      const success = await crmDataService.deleteCSActivity(id); 
      if (success) {
        // 🚨 [수정] true를 넣어서 로딩바 없이 배경에서 데이터만 쏙 바꿔치기합니다.
        loadData(true); 
      } else {
        alert("삭제에 실패했습니다.");
      }
    } catch (error) {
      console.error("CS 삭제 중 오류 발생:", error);
    }
  };

  // 2. CS 활동을 매출예정표(Deals)로 전달하는 함수
  const handleConvertToDeal = async (activity: CSActivity) => {
    // 1. 매출 예정표에 추가
    const success = await crmDataService.saveDealFromCS(activity); 
    
    if (success) {
      // 🚨 2. [핵심] 원본 CS 데이터의 상태를 '매출등록'으로 변경해서 알림에서 지워버림!
      await crmDataService.saveCSActivity({
        ...activity,
        status: '매출등록'
      });

      alert("매출예정표로 등록이 완료되었습니다. (알림이 해제됩니다)");
      loadData(); 
    } else {
      alert("매출 등록에 실패했습니다. 데이터를 다시 확인해주세요.");
    }
  };

  


  // ==================================================================================
  // [SECTION 5] 렌더링 (Render UI)
  // ==================================================================================

  // 5-1. 로그인 안 된 경우 -> 로그인 화면 표시
  if (!isAuthenticated || !currentUser) {
    return (
      <div className="min-h-screen bg-slate-100 text-slate-900 font-sans p-6 md:p-10 flex items-center justify-center">
        <SupportAuth onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  // ==================================================================================
  // 🚀 [추가] 탭 노출 권한 필터링 로직 (매트릭스와 완벽 연동)
  // ==================================================================================
  const NAV_TABS = [
    { id: 'dashboard', label: '홈' },
    { id: 'shared_calendar', label: '통합 일정' }, // 📅 [신규 추가]
    { id: 'sales_target', label: '매출목표' },
    { id: 'forecast', label: '매출예정표' },
    { id: 'forecast_detail', label: '목표대비실적' },
    { id: 'pipeline', label: '영업진행상황' },
    { id: 'companies', label: '거래처 및 고객관리' }, 
    { id: 'lost_accounts', label: '이탈거래처' }, 
    { id: 'cs_management', label: 'CS팀 활동관리' },
    { id: 'project_progress', label: 'WBS' },
    { id: 'reports', label: '보고서' },
    { id: 'activity_log', label: '활동로그' },
  ];

  // 사용자의 권한을 체크하여 탭 메뉴를 필터링합니다. (스위치가 꺼져있으면 아예 배열에서 뺌)
  const visibleTabs = NAV_TABS.filter(tab => {
    const perms = currentUser?.permissions || {};
    
    // 관리자는 무조건 프리패스!
    if (currentUser?.role === 'admin') return true;

    // 각 탭별로 '메뉴 노출 설정' 스위치가 켜져있는지 확인합니다.
    switch(tab.id) {
      case 'shared_calendar': return perms.show_schedule !== false; // 📅 [신규 추가]
      case 'sales_target': return perms.show_sales_target !== false; 
      case 'forecast': return perms.show_forecast !== false;
      case 'forecast_detail': return perms.show_sales_analysis !== false;
      case 'pipeline': return perms.show_pipeline !== false;
      case 'companies': return perms.show_contacts !== false;
      case 'lost_accounts': return perms.show_lost_account !== false;
      case 'cs_management': return perms.show_cs_activity !== false;
      case 'project_progress': return perms.show_wbs !== false;
      // 홈, 보고서, 활동로그 등은 기본적으로 항상 노출됩니다.
      // (홈의 실시간 피드 숨김 로직은 Dashboard 컴포넌트 안에서 currentUser를 받아 처리합니다.)
      default: return true;
    }
  });


  // 5-2. 메인 앱 화면 (로그인 후)
  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 font-sans">
      
      {/* ------------------------------------------------ */}
      {/* 🚀 (A) 사이드바 (Navigation) - 토글 기능으로 숨김 처리 */}
      {/* ------------------------------------------------ */}
      {/* 🚀 사이드바 주석 처리 (코드 파일은 유지, 화면에서 제거) */}
      {/* <div className={`fixed top-0 left-0 h-screen w-64 bg-white border-r border-slate-200 shadow-2xl z-[60] transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar currentView={currentView} onViewChange={setCurrentView} user={currentUser} />
      </div> */}
      
      {/* ------------------------------------------------ */}
      {/* (B) 메인 콘텐츠 영역 (사이드바가 열릴 때만 밀려남) */}
      {/* ------------------------------------------------ */}
      {/* 🚀 사이드바 제거하여 왼쪽 여백 삭제! 전체 화면 사용 */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ml-0`}>
        
        {/* ================================================================================== */}
        {/* 🚀 [SECTION B-0] 상단 내비게이션 바 (1층 헤더) */}
        {/* 이 부분에 방금 만든 visibleTabs를 매핑하여, 스위치가 켜진 탭만 보여줍니다! */}
        {/* ================================================================================== */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 lg:px-8 shadow-sm w-full">
          
          {/* 왼쪽 파트: 햄버거 메뉴 + 로고 + 가로 탭 리스트 */}
          <div className="flex items-center h-full flex-1 overflow-x-auto custom-scrollbar no-scrollbar gap-2 pr-4">
            
            {/* 사이드바 토글 (햄버거 버튼) */}
            {/* 사이드바 미사용으로 주석 */}
            {/* <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors mr-2 shrink-0">
              <Menu size={22} />
            </button> */}

            {/* 로고 */}
            <div className="font-black text-blue-600 text-lg mr-6 shrink-0 whitespace-nowrap cursor-pointer" onClick={() => setCurrentView('dashboard')}>
              IEG CRM
            </div>

            {/* 🚀 메인 탭 메뉴들 (권한에 따라 필터링된 배열 사용!) */}
            <div className="flex items-center gap-1 h-full">
              {visibleTabs.map(tab => (
                <button 
                  key={tab.id}
                  onClick={() => setCurrentView(tab.id as any)}
                  className={`px-4 py-2 rounded-[14px] text-[13px] font-black transition-all whitespace-nowrap ${currentView === tab.id ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* 🚀 오른쪽 파트: 알림, 관리자, 마이페이지 */}
          <div className="flex items-center gap-3 border-l border-slate-200 pl-6 ml-2 shrink-0 h-10">
            
            {/* 관리자 모드 버튼 */}
            <PermissionGate user={currentUser} action="admin_page">
              <button 
                onClick={() => setCurrentView('admin_mode')} 
                className={`p-2 rounded-lg transition-all ${currentView === 'admin_mode' ? 'bg-blue-100 text-blue-600' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                title="관리자 페이지"
              >
                <Shield size={18} />
              </button>
            </PermissionGate>

            {/* 🔔 알림 종버튼 및 팝업창 */}
            <div className="relative flex items-center" ref={notiRef}>
              <button 
                onClick={() => setShowAlerts(!showAlerts)} 
                className={`p-2 rounded-lg transition-all relative ${alerts.length > 0 ? 'text-orange-500 animate-pulse bg-orange-50' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
              >
                <Bell size={18} />
                {alerts.length > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black w-4 h-4 flex items-center justify-center rounded-full border-2 border-white">{alerts.length}</span>}
              </button>

              {/* 알림 팝업창 */}
              {showAlerts && (
                <div className="absolute top-full right-0 mt-3 w-80 max-h-[400px] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200 z-[100] p-4 flex flex-col gap-3 animate-fade-in-up">
                  <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                    <h3 className="text-sm font-black text-slate-800">알림 센터 센터</h3>
                    <span className="text-[10px] text-slate-400 font-bold">{alerts.length}건</span>
                  </div>

                  {alerts.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-4 font-bold">새로운 알림이 없습니다.</p>
                  ) : (
                    alerts.map((alert, idx) => (
                      <div key={idx} className={`p-3 rounded-xl border shadow-sm ${alert.type === 'danger' ? 'bg-red-50 border-red-100' : alert.type.startsWith('cs_') ? 'bg-blue-50/50 border-blue-100' : 'bg-slate-50 border-slate-100'}`}>
                        <h4 className="text-xs font-bold text-slate-900 leading-tight">{alert.title}</h4>
                        <p className="text-[11px] text-slate-600 mt-1 font-medium">{alert.msg}</p>
                        <div className="flex justify-end gap-2 mt-3">
                          {/* 🚨 [핵심 수정] 알림창의 버튼에 바통(targetCsActivity)을 쥐어줍니다! */}
                          {alert.type === 'cs_warning' && (
                            <button 
                              onClick={() => { 
                                setTargetCsActivity(alert.csActivity); 
                                setCurrentView('cs_management'); 
                                setShowAlerts(false); 
                              }} 
                              className="text-[10px] px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 font-black shadow-sm transition-all"
                            >
                              지정하러 가기
                            </button>
                          )}
                          {alert.type === 'cs_info' && (
                            <button onClick={() => { if(window.confirm('매출예정표에 등록하시겠습니까?')) { handleConvertToDeal(alert.csActivity); setShowAlerts(false); } }} className="text-[10px] px-3 py-1.5 bg-blue-600 border border-blue-600 rounded-lg text-white hover:bg-blue-700 font-black shadow-md shadow-blue-200 transition-all active:scale-95">매출 등록</button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="w-[1px] h-5 bg-slate-200 mx-1" />

            {/* 마이페이지 (설정) 버튼 */}
            <button 
              onClick={() => setCurrentView('settings')}
              className="flex items-center gap-3 p-1 pr-3 rounded-full hover:bg-slate-100 transition-all border border-transparent hover:border-slate-200 group"
              title="마이페이지 (설정)"
            >
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-black text-sm group-hover:bg-blue-600 group-hover:text-white transition-colors">
                {currentUser?.name?.charAt(0) || 'U'}
              </div>
              <div className="text-left hidden lg:block">
                <p className="text-[13px] font-black text-slate-800 leading-tight">{currentUser?.name}</p>
                <p className="text-[10px] font-bold text-slate-500 leading-tight">{currentUser?.team_name} / {currentUser?.grade}</p>
              </div>
            </button>
          </div>
        </header>

        {/* ================================================================================== */}
        {/* 🚀 [SECTION B-2] 실제 화면(Page)이 렌더링되는 영역 */}
        {/* ================================================================================== */}
        <main className="p-6 md:p-8 flex-1 flex flex-col">
          
          {/* 각 탭별 상단 타이틀 및 현재 시간 표시 구역 (대시보드는 제외) */}
          {currentView !== 'dashboard' && (
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-4">
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                    {currentView === 'shared_calendar' ? '영업/CS 통합 일정' :  // 📅 [신규 추가]
                     currentView === 'contacts' ? '고객 데이터베이스' : 
                     currentView === 'pipeline' ? '영업 파이프라인' : 
                     currentView === 'sales_target' ? '매출 목표 (Target)' :
                     currentView === 'forecast' ? '매출 예정표 (Forecast)' : 
                     currentView === 'forecast_detail' ? '매출 집계 및 통계 (Analysis)' : 
                     currentView === 'sales_support' ? '영업 문서 지원 (AI Docs)' : 
                     currentView === 'activity_log' ? '활동 로그 (Notion)' : 
                     currentView === 'reports' ? '보고서 센터' :
                     currentView === 'project_progress' ? '팀별 진행상황 (WBS)' :
                     currentView === 'cs_management' ? 'CS팀 활동 관리' : 
                     currentView === 'settings' ? '마이페이지 (설정)' : 
                     currentView === 'companies' ? '거래처 및 고객 관리' :
                     currentView === 'lost_accounts' ? '이탈 거래처 관리' : 
                     currentView === 'g2b' ? '나라장터 수집 관리' : 'IEG CRM'}
                  </h1>
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm">
                     <Clock size={14} className="text-blue-600" />
                     {currentTime}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 콘텐츠 렌더링 영역 */}
          <div className="min-h-[calc(100vh-140px)]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full space-y-4 pt-20">
                <Loader2 size={48} className="animate-spin text-blue-600" />
                <p className="text-slate-500 font-bold">데이터 로딩 중...</p>
              </div>
            ) : (
              <div className="animate-fade-in-up flex-1">
                {/* 🚀 1. 대시보드 (currentUser 전달 추가 - 내부 피드 숨김 기능 사용을 위해) */}
                {currentView === 'dashboard' && <Dashboard globalRefreshKey={globalRefreshKey} onNavigate={(pageName) => setCurrentView(pageName as any)} currentTime={currentTime} currentUser={currentUser} />}
                
                {/* 📅 [신규 추가] 1-B. 통합 일정 (Shared Calendar) */}
                {currentView === 'shared_calendar' && <UnifiedCalendar currentUser={currentUser} globalRefreshKey={globalRefreshKey} />}

                {/* 🚀 2. 거래처 관리 (CompanyList) */}
                {currentView === 'companies' && <CompanyList globalRefreshKey={globalRefreshKey} onNavigate={(viewName: string) => setCurrentView(viewName)} currentUser={currentUser} />}

                {/* 3. 고객 관리 (ContactList) */}
                {currentView === 'contacts' && <ContactList currentUser={currentUser} contacts={contacts} salesReps={salesReps} deals={deals} onUpdateContacts={setContacts} globalRefreshKey={globalRefreshKey} />}
                
                {/* 4. 영업 파이프라인 (DealBoard) */}
                {currentView === 'pipeline' && <DealBoard deals={deals} contacts={contacts} currentUser={currentUser} globalRefreshKey={globalRefreshKey} />}
                
                {/* 🎯 5. 팀별 진행상황 (WBS) */}
                {currentView === 'project_progress' && (
                  <ProjectProgressBoard 
                    projects={wbsProjects} 
                    currentUser={currentUser}
                    globalRefreshKey={globalRefreshKey} 
                    onRefresh={() => loadData(true)} 
                  />
                )}
                
                {/* 6. 매출 목표 (SalesTarget) */}
                {currentView === 'sales_target' && <SalesTarget currentUser={currentUser} globalRefreshKey={globalRefreshKey} />}
                
                {/* 7. 매출 예정표 (SalesForecast) */}
                {currentView === 'forecast' && <SalesForecast deals={deals} contacts={contacts} salesReps={salesReps} currentUser={currentUser} onUpdateContact={(c) => setContacts(contacts.map(con => con.id === c.id ? c : con))} onUpdateDeals={setDeals} globalRefreshKey={globalRefreshKey} />}
                
                {/* 8. 매출 상세 분석 (SalesAnalysis) */}
                {currentView === 'forecast_detail' && (<SalesAnalysis salesReps={salesReps} currentUser={currentUser} globalRefreshKey={globalRefreshKey} />)}
                
                {/* 9. 이탈 거래처 */} 
                {currentView === 'lost_accounts' && (<LostDeals currentUser={currentUser} key={globalRefreshKey} onRefresh={() => setGlobalRefreshKey(prev => prev + 1)} />)}
                
                {/* 10. 영업 지원 (SalesSupport) */}
                {currentView === 'sales_support' && <SalesSupport user={currentUser} deals={deals} contacts={contacts} globalRefreshKey={globalRefreshKey} />}
                
                {/* 11. 활동 로그 & 보고서 */}
                {currentView === 'activity_log' && <ActivityLog user={currentUser} deals={deals} onAddActivity={(a) => setActivities([...activities, a])} globalRefreshKey={globalRefreshKey} />}
                {currentView === 'reports' && <Reports contacts={contacts} deals={deals} activities={activities} globalRefreshKey={globalRefreshKey} />}
                
                {/* 12. CS 관리 화면 */}
                {currentView === 'cs_management' && (
                  <CSActivityManagement
                    activities={csActivities}
                    contacts={contacts}
                    
                    user={currentUser}
                    salesReps={salesReps}
                    globalRefreshKey={globalRefreshKey}
                    targetActivity={targetCsActivity} 
                    onClearTarget={() => setTargetCsActivity(null)} 
                    onAddActivity={async (newActivity) => {
                      const success = await crmDataService.saveCSActivity(newActivity);
                      if (success) loadData(true); 
                      else alert("CS 활동 저장에 실패했습니다.");
                    }}
                    onDeleteActivity={handleDeleteCSActivity}
                    onConvertToDeal={handleConvertToDeal}
                  />
                )}

                {/* 13. 마이페이지 / 설정 (Settings) */}
                {currentView === 'settings' && <Settings
                  currentUser={currentUser}
                  globalRefreshKey={globalRefreshKey}
                  onUpdateUser={async (u) => {
                    if (!authUsername) return;
                    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        username: authUsername,
                        name: u.name,
                        email: u.email,
                        phone: u.phone,
                      })
                    });
                    const data = await response.json().catch(() => ({}));
                    if (!response.ok) throw new Error(data?.detail || '프로필 저장 실패');
                    const dbUser = data?.user || {};
                    const updatedUser = {
                      id: dbUser.id,
                      username: dbUser.username,
                      name: dbUser.name || u.name,
                      email: dbUser.email || u.email,
                      phone: dbUser.phone || u.phone,
                      role: dbUser.role || currentUser.role, 
                      permissions: dbUser.permissions || currentUser.permissions, 
                      team_name: dbUser.team_name || currentUser.team_name, 
                      grade: dbUser.grade || currentUser.grade
                    };
                    setCurrentUser(updatedUser);
                    localStorage.setItem('ieg_user_profile', JSON.stringify(updatedUser));
                  }}
                  onLogout={handleLogout}
                />}

                {/* 🚨 [신규 추가] 14. 나라장터 수집 관리 화면 */}
                {currentView === 'g2b' && <G2BAdminBoard />}
              </div>
            )}
          </div>
        </main>
      </div>

      
      
      {/* 🚀 플로팅 AI 어시스턴트 */}
      <div className="fixed bottom-6 right-6 z-50">
         <AIAssistant contacts={contacts} deals={deals} internalHistory={internalHistory} />
      </div>

      {/* 🚀 관리자 모드 레이어 */}
      {currentView === 'admin_mode' && (
        <PermissionGate user={currentUser} action="admin_page">
          <div className="fixed inset-0 z-[9999] bg-white animate-fade-in">
            <AdminLayout currentUser={currentUser} onBackToMain={() => setCurrentView('dashboard')}
            globalRefreshKey={globalRefreshKey}
             />
          </div>
        </PermissionGate>
      )}
      
    </div>
  );
};

export default App;